import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAlterarTaxaServicoTotemMutation } from '@/api'
import { atualizarKiosk } from '@/store/self-service-kiosk-slice'
import type { RootState } from '@/store'

export interface TaxaServicoTotem {
    taxaServico: number
    taxaServicoAnterior: number
    dispensada: boolean
    podeAlterar: boolean
    enviando: boolean
    alterando: boolean
    erro: string | null
    limparErro: () => void
    dispensar: () => Promise<void>
    reaplicar: () => Promise<void>
}

export function useTaxaServicoTotem(permitirDispensa: boolean): TaxaServicoTotem {
    const dispatch = useDispatch()
    const order = useSelector((state: RootState) => state.selfServiceKiosk.order)
    const [alterarTaxa, { isLoading }] = useAlterarTaxaServicoTotemMutation()
    const [erro, setErro] = useState<string | null>(null)
    const [alterando, setAlterando] = useState(false)

    const idPedido = order?.idPedidoIntegracao ?? null
    const bloqueado = order?.bloqueadoNoTotem === true
    const taxaServico = order?.taxaServico ?? 0
    const dispensada = order?.taxaServicoDispensada ?? false
    const valorRecebido = order?.valorRecebido ?? 0

    const taxaServicoAnterior = order?.taxaServicoDispensadaValor ?? 0

    const aplicar = useCallback(
        async (acao: 'SINCRONIZAR' | 'DISPENSAR' | 'REAPLICAR'): Promise<void> => {
            if (!idPedido) return

            const resultado = await alterarTaxa({ idPedidoIntegracao: idPedido, acao }).unwrap()

            dispatch(
                atualizarKiosk({
                    order: {
                        ...order!,
                        taxaServico: resultado.taxaServico,
                        subtotal: resultado.subtotal,
                        desconto: resultado.desconto,
                        total: resultado.total,
                        taxaServicoDispensada: resultado.dispensada,
                        taxaServicoDispensadaValor: resultado.taxaServicoDispensadaValor,
                        valorRecebido: resultado.valorRecebido,
                        itens: resultado.itens ?? order!.itens,
                    },
                }),
            )
        },
        [alterarTaxa, dispatch, idPedido, order],
    )

    const sincronizadoRef = useRef<number | null>(null)
    useEffect(() => {
        if (!idPedido || sincronizadoRef.current === idPedido) return
        if (bloqueado) return

        sincronizadoRef.current = idPedido
        void aplicar('SINCRONIZAR').catch((e) => {
            console.error('Falha ao sincronizar a taxa de serviço:', e)
        })
    }, [idPedido, bloqueado])

    const alternar = useCallback(
        async (acao: 'DISPENSAR' | 'REAPLICAR'): Promise<void> => {
            setErro(null)
            setAlterando(true)
            try {
                await aplicar(acao)
            } catch (e) {
                console.error('Falha ao alterar a taxa de serviço:', e)
                const mensagem = (e as { data?: { mensagem?: string } })?.data?.mensagem
                setErro(mensagem ?? 'Não foi possível alterar a taxa de serviço. Tente novamente ou chame um atendente.')
            } finally {
                setAlterando(false)
            }
        },
        [aplicar],
    )

    return {
        taxaServico,
        taxaServicoAnterior,
        dispensada,
        podeAlterar: permitirDispensa && !!idPedido && !bloqueado && valorRecebido <= 0 && (taxaServico > 0 || dispensada),
        enviando: isLoading,
        alterando,
        erro,
        limparErro: () => setErro(null),
        dispensar: () => alternar('DISPENSAR'),
        reaplicar: () => alternar('REAPLICAR'),
    }
}
